import React, { useEffect, useState } from "react";
import axios from "axios";
import "../styles/AdminFeedbacks.css";

const AdminFeedbacks = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("table");
  const [stats, setStats] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    rating: 5,
  });

  /* ---------------- FETCH DATA ---------------- */

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/api/v1/feedbacks/getFeedback");
      setFeedbacks(res.data);
    } catch {
      alert("Failed to fetch feedbacks");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get("/api/v1/feedbacks/feedback-stats");
      setStats(res.data);
    } catch {
      alert("Failed to fetch stats");
    }
  };

  useEffect(() => {
    fetchFeedbacks();
    fetchStats();
  }, []);

  /* ---------------- FORM HANDLERS ---------------- */

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
      rating: 5,
    });
    setEditingFeedback(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      if (editingFeedback) {
        await axios.put(
          `/api/v1/feedbacks/updateFeedback/${editingFeedback._id}`,
          formData
        );
        alert("Feedback updated");
      } else {
        await axios.post("/api/v1/feedbacks/feedback", formData);
        alert("Feedback added");
      }

      resetForm();
      setIsModalOpen(false);
      fetchFeedbacks();
      fetchStats();
    } catch {
      alert("Operation failed");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- ACTIONS ---------------- */

  const handleEdit = (feedback) => {
    setEditingFeedback(feedback);
    setFormData(feedback);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this feedback?")) return;

    try {
      await axios.delete(`/api/v1/feedbacks/deleteFeedback/${id}`);
      setFeedbacks((prev) => prev.filter((f) => f._id !== id));
      fetchStats();
    } catch {
      alert("Delete failed");
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(`/api/v1/feedbacks/approveFeedback/${id}`);
      setFeedbacks((prev) =>
        prev.map((f) => (f._id === id ? { ...f, approved: true } : f))
      );
      fetchStats();
    } catch {
      alert("Approval failed");
    }
  };

  /* ---------------- RENDER ---------------- */

  return (
    <div className="admin-feedbacks-container">
      <h2>Feedback Management</h2>

      <button onClick={() => setIsModalOpen(true)}>Add Feedback</button>
      <button
        className="secondary"
        onClick={() => setViewMode(viewMode === "table" ? "card" : "table")}
        style={{ marginLeft: 10 }}
      >
        Switch to {viewMode === "table" ? "Card" : "Table"} View
      </button>

      {/* STATS */}
      <div className="stats-container">
        <div className="stat-card">
          <h4>Total</h4>
          <p>{stats.totalFeedbacks || 0}</p>
        </div>
        <div className="stat-card">
          <h4>Approved</h4>
          <p>{stats.approvedFeedbacks || 0}</p>
        </div>
        <div className="stat-card">
          <h4>Pending</h4>
          <p>{stats.unapprovedFeedbacks || 0}</p>
        </div>
        <div className="stat-card">
          <h4>Avg Rating</h4>
          <p>{stats.avgRating || 0} ⭐</p>
        </div>
      </div>

      {/* TABLE VIEW */}
      {viewMode === "table" && (
        <table className="feedback-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Message</th>
              <th>Rating</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {feedbacks.map((f) => (
              <tr key={f._id}>
                <td>{f.name}</td>
                <td>{f.email}</td>
                <td>{f.message}</td>
                <td>{"⭐".repeat(f.rating)}</td>
                <td className="feedback-actions">
                  {!f.approved && (
                    <button
                      className="approve"
                      onClick={() => handleApprove(f._id)}
                    >
                      Approve
                    </button>
                  )}
                  <button onClick={() => handleEdit(f)}>Edit</button>
                  <button
                    className="danger"
                    onClick={() => handleDelete(f._id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* CARD VIEW */}
      {viewMode === "card" && (
        <div className="feedback-card-container">
          {feedbacks.map((f) => (
            <div className="feedback-card" key={f._id}>
              <h3>{f.name}</h3>
              <p>{f.message}</p>
              <p className="rating">{"⭐".repeat(f.rating)}</p>
              <div className="card-actions">
                {!f.approved && (
                  <button
                    className="approve"
                    onClick={() => handleApprove(f._id)}
                  >
                    Approve
                  </button>
                )}
                <button onClick={() => handleEdit(f)}>Edit</button>
                <button className="danger" onClick={() => handleDelete(f._id)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MODAL */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{editingFeedback ? "Edit Feedback" : "Add Feedback"}</h3>

            <form onSubmit={handleSubmit}>
              <input
                name="name"
                placeholder="Name"
                value={formData.name}
                onChange={handleChange}
                required
              />
              <input
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                required
              />
              <input
                name="phone"
                placeholder="Phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
              <input
                name="subject"
                placeholder="Subject"
                value={formData.subject}
                onChange={handleChange}
                required
              />
              <textarea
                name="message"
                placeholder="Message"
                value={formData.message}
                onChange={handleChange}
                required
              />

              <select
                name="rating"
                value={formData.rating}
                onChange={handleChange}
              >
                {[1, 2, 3, 4, 5].map((r) => (
                  <option key={r} value={r}>
                    {r} Star
                  </option>
                ))}
              </select>

              <div className="modal-actions">
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit">
                  {editingFeedback ? "Update" : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading && <div className="loader">Loading...</div>}
    </div>
  );
};

export default AdminFeedbacks;
